import React, { useState, useEffect, useRef } from 'react';
import { buildApiUrl, buildWebSocketUrl } from './config';
import { useAuth } from './AuthContext';

const StudentConnection = () => {
  const { teacherEmail, role, authorInfo, school_id } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [screenCaptureEnabled, setScreenCaptureEnabled] = useState(false);
  const wsRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);

  // Use actual student information from auth context
  const studentId = school_id ? `5C_student_${Date.now()}` : (teacherEmail?.replace('@sherborneqatar.org', '')?.replace(/\./g, '_') + '_student_' + Date.now());
  const studentName = authorInfo?.full_name || authorInfo?.name || 'Student';

  // Get form information from student's data
  const getFormId = () => {
    // Extract form from user profile or default
    return '5C'; // Default to 5C for Sarah Hesham
  };

  const connectToTeacher = async () => {
    if (!teacherId.trim()) {
      setError('Please enter a teacher ID');
      return;
    }

    try {
      setError('');
      setStatus('Connecting to teacher...');

      // Use the new server-based screen capture approach
      const formId = getFormId();
      const fullStudentId = `${formId}_student_${Date.now()}`;

      const wsUrl = `${buildWebSocketUrl()}/ws/student-live/${teacherId}/${fullStudentId}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setIsConnected(true);
        setStatus(`Connected to teacher: ${teacherId}`);
        console.log('Connected to teacher monitoring WebSocket');

        // Send initial student info
        ws.send(JSON.stringify({
          type: 'student_info',
          student_id: fullStudentId,
          student_name: studentName,
          form: formId,
          timestamp: new Date().toISOString()
        }));

        // Start heartbeat
        heartbeatIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'heartbeat', timestamp: new Date().toISOString() }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Received from teacher:', data.type);
          
          if (data.type === 'heartbeat_ack') {
            // Heartbeat acknowledged
            setStatus(`Connected to teacher: ${teacherId} ✓`);
          }
        } catch (err) {
          console.log('Non-JSON message:', event.data);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setError('Connection error occurred');
        setStatus('Connection failed');
      };

      ws.onclose = () => {
        setIsConnected(false);
        setIsCapturing(false);
        setScreenCaptureEnabled(false);
        setStatus('Disconnected from teacher');
        
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
        }
      };

      wsRef.current = ws;

    } catch (error) {
      console.error('Failed to connect to teacher:', error);
      setError('Failed to connect to teacher: ' + error.message);
      setStatus('Connection failed');
    }
  };

  const startScreenCapture = async () => {
    if (!isConnected || !teacherId) {
      setError('Please connect to a teacher first');
      return;
    }

    try {
      setError('');
      setStatus('Starting screen capture...');

      // Start screen capture via the dedicated server
      const formId = getFormId();
      const fullStudentId = `${formId}_student_${Date.now()}`;

      const response = await fetch(buildApiUrl(`/api/start-student-screen-capture/${fullStudentId}/${teacherId}`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        setIsCapturing(true);
        setScreenCaptureEnabled(true);
        setStatus('Screen capture started successfully! Teacher can now see your screen.');
        console.log('Screen capture started:', result);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to start screen capture');
      }

    } catch (error) {
      console.error('Screen capture error:', error);
      setError(`Screen capture failed: ${error.message}`);
      setStatus('Screen capture failed');
      
      // Provide helpful guidance
      if (error.message.includes('server not available')) {
        setError('Screen capture server is not running. Please ask your administrator to start it.');
      }
    }
  };

  const stopScreenCapture = async () => {
    try {
      setStatus('Stopping screen capture...');

      const formId = getFormId();
      const fullStudentId = `${formId}_student_${Date.now()}`;

      const response = await fetch(buildApiUrl(`/api/stop-student-screen-capture/${fullStudentId}`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        setIsCapturing(false);
        setScreenCaptureEnabled(false);
        setStatus('Screen capture stopped');
      } else {
        throw new Error('Failed to stop screen capture');
      }

    } catch (error) {
      console.error('Error stopping screen capture:', error);
      setError('Failed to stop screen capture: ' + error.message);
    }
  };

  const disconnect = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    
    if (isCapturing) {
      stopScreenCapture();
    }
    
    setIsConnected(false);
    setIsCapturing(false);
    setScreenCaptureEnabled(false);
    setStatus('Disconnected');
    setError('');
  };

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  return (
    <div className="student-connection p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-4">Server-Based Screen Sharing</h2>
      <p className="text-gray-600 mb-4">
        Connect to your teacher for live monitoring using our dedicated screen capture server.
      </p>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}
      
      {status && (
        <div className="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded">
          {status}
        </div>
      )}
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Teacher ID:
          </label>
          <input
            type="text"
            value={teacherId}
            onChange={(e) => setTeacherId(e.target.value)}
            placeholder="Enter teacher ID (e.g., hisham_shehadeh)"
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isConnected}
          />
        </div>
        
        <div className="flex space-x-2">
          {!isConnected ? (
            <button
              onClick={connectToTeacher}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!teacherId.trim()}
            >
              Connect to Teacher
            </button>
          ) : (
            <button
              onClick={disconnect}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Disconnect
            </button>
          )}
        </div>
        
        {isConnected && (
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">Screen Capture Control</h3>
            
            {!isCapturing ? (
              <button
                onClick={startScreenCapture}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Start Screen Capture
              </button>
            ) : (
              <button
                onClick={stopScreenCapture}
                className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                Stop Screen Capture
              </button>
            )}
            
            {screenCaptureEnabled && (
              <div className="p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                ✅ Screen capture is active. Your teacher can see your screen.
              </div>
            )}
          </div>
        )}
        
        <div className="mt-6 p-4 bg-gray-100 rounded">
          <h4 className="font-semibold mb-2">How it works:</h4>
          <ul className="text-sm text-gray-700 space-y-1">
            <li>• Uses a dedicated server to capture your screen (no browser permissions needed)</li>
            <li>• Works automatically once connected to a teacher</li>
            <li>• More reliable than browser-based screen sharing</li>
            <li>• Teacher receives live updates of your screen</li>
          </ul>
        </div>
        
        <div className="text-sm text-gray-500">
          <p>Student: {studentName}</p>
          <p>Form: {getFormId()}</p>
          {isConnected && <p>Status: Connected to {teacherId}</p>}
        </div>
      </div>
    </div>
  );
};

export default StudentConnection;